/* De media in een formulier (ADR 0024): de knop "Kies uit media" bij een afbeeldingsveld, en het
 * uploaden van nieuwe bestanden. Het scherm Media (Media.tsx) gebruikt hetzelfde uploaden en dezelfde
 * miniatuur. Dit bestand tekent alleen. Wat een bestand mag zijn, hoe het in de opslag heet en wat er
 * wordt geschreven, staat in data/media.ts.
 *
 * De kiezer laat alleen beelden zien: een pdf is geen afbeelding. Hij vult het pad in zoals het in de
 * content hoort, en zelf typen blijft kunnen. Uploaden gaat bestand voor bestand: eerst het bestand
 * naar de opslag, dan de rij in de bibliotheek. Er is hier geen knop om een bestand te verwijderen. */

import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, Copy, FileText, Images, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useMedia, useMediaRij, useMediaUpload, useRechten } from './hooks';
import {
  VOORVOEGSEL, adresVan, adresVanPad, grootte, isBeeld, isGekozen, keur, kiesbaar, naamVan, padInContent, zoek, zonderRijZin,
  type Keuring, type MediaBestand,
} from './data/media';

/** Zoveel bestanden staan er in één keer; de rest komt met een knop. Een miniatuur is het hele beeld
 *  uit de opslag: alles tegelijk laden is op een telefoon te zwaar. */
export const STAP = 24;
/* Waar de site de beschrijving van een bestand (alt_text) gebruikt als de beschrijving van het beeld
 * (besluit Stan, 10-10-2026; nagelezen in de site-repo, tak v2-logo-en-alt, 11-10-2026: de plekken waar
 * MediaImage `altFromLibrary` krijgt). Op die plekken had de pagina zelf alleen een titel. Waar de titel
 * of de naam de beste beschrijving is (een kaart, een teamfoto, een logo), blijft die staan. */
export const BESCHRIJVING_PLEKKEN = 'De site gebruikt de beschrijving bij de foto bovenaan een pagina, een nieuwsbericht of een kennisartikel (niet bij die van de homepage), bij de brede foto op de pagina van een energieoplossing, dienst of branche, bij de tekening op de homepage, en bij een beeld in een schuifbeeld waar geen eigen beschrijving is ingevuld. Op kaarten, bij teamfoto\'s en bij logo\'s blijft de titel of de naam de beschrijving.';
/** Boven de bibliotheek op het scherm Media: wat een beschrijving is en waar de site hem gebruikt. */
export const BESCHRIJVING_OP_DE_SITE = `Een beschrijving is één zin over wat er op het beeld staat, voor wie het niet kan zien. ${BESCHRIJVING_PLEKKEN}`;
export const BESCHRIJVING_UITLEG = 'Eén zin over wat er op het beeld staat, voor wie het niet kan zien. Waar de site de beschrijving gebruikt, staat bovenaan de bibliotheek.';
const BESCHRIJVING_BIJ_UPLOAD = `Geef een beeld een beschrijving: één zin over wat erop staat, voor wie het niet kan zien. ${BESCHRIJVING_PLEKKEN} Het is niet verplicht en kan later nog.`;
const NIEUW_UITLEG = 'Een nieuw bestand verandert niets op de site tot je het ergens gebruikt.';

/** De knop die het pad op het klembord zet. Lukt dat niet, dan staat het pad in de melding om zelf te kopiëren. */
export function KopieerPad({ pad }: { pad: string }) {
  const { toast } = useToast();
  const kopieer = async () => {
    try {
      await navigator.clipboard.writeText(pad);
      toast({ title: 'Pad gekopieerd', description: pad });
    } catch {
      // Geen foutmelding met een knop naar de beheerder: het pad staat erbij en is met de hand te kopiëren.
      toast({ title: 'Kopiëren is niet gelukt', description: `Selecteer het pad en kopieer het zelf: ${pad}` });
    }
  };
  return <Button type="button" variant="outline" size="sm" onClick={kopieer}><Copy className="mr-1 h-4 w-4" /> Kopieer pad</Button>;
}

/** Het beeld zelf, klein. Een pdf krijgt een icoon. */
export function Miniatuur({ bestand, className = '' }: { bestand: Pick<MediaBestand, 'storage_path' | 'alt_text'>; className?: string }) {
  if (!isBeeld(bestand)) {
    return (
      <span className={`flex items-center justify-center rounded border bg-muted text-muted-foreground ${className}`} data-miniatuur="pdf">
        <FileText className="h-8 w-8" aria-hidden /> <span className="sr-only">pdf</span>
      </span>
    );
  }
  return <img src={adresVan(bestand)} alt={bestand.alt_text ?? ''} loading="lazy" decoding="async" className={`rounded border bg-muted object-contain ${className}`} data-miniatuur="beeld" />;
}

/* ── Uploaden ──────────────────────────────────────────────────────────────── */

interface Wachtend {
  sleutel: string;
  bestand: File;
  keuring: Keuring;
  naam: string;
  beschrijving: string;
  /** wacht: nog te uploaden. zonder_rij: het bestand staat in de opslag, de rij is er nog niet. */
  staat: 'wacht' | 'bezig' | 'gelukt' | 'zonder_rij';
  /** Het pad in de bibliotheek, zodra het bestand in de opslag staat. */
  pad?: string;
  /** Het pad zoals het in de content hoort, zodra de rij er is. */
  inContent?: string;
  /** Waarom het uploaden of het aanmaken van de rij niet lukte. */
  fout?: string;
  /** Een voorbeeld van het gekozen beeld, uit het geheugen van de browser. */
  voorbeeld?: string;
}

let volgnummer = 0;
const redenVan = (fout: unknown): string => (fout instanceof Error ? fout.message : String(fout));

/** Bestanden kiezen of erheen slepen, keuren, een naam en een beschrijving geven en uploaden. In de
 *  kiezer van een afbeeldingsveld (`alleenBeelden`) kan er geen pdf in, en zet `kies` een geüpload
 *  beeld meteen in het veld. */
export function Uploaden({ alleenBeelden = false, kies }: { alleenBeelden?: boolean; kies?: (pad: string) => void }) {
  const { toast } = useToast();
  const [lijst, setLijst] = useState<Wachtend[]>([]);
  const [bezig, setBezig] = useState(false);
  const [boven, setBoven] = useState(false);
  const invoer = useRef<HTMLInputElement>(null);
  const voorbeelden = useRef<string[]>([]);
  const uploaden = useMediaUpload();
  const rijMaken = useMediaRij();

  // De voorbeelden staan in het geheugen van de browser: geef ze terug als het onderdeel verdwijnt.
  useEffect(() => {
    const adressen = voorbeelden.current;
    return () => adressen.forEach((adres) => URL.revokeObjectURL(adres));
  }, []);

  const werkBij = (sleutel: string, over: Partial<Wachtend>) => setLijst((l) => l.map((w) => (w.sleutel === sleutel ? { ...w, ...over } : w)));

  const voegToe = (bestanden: File[]) => {
    const nieuw = bestanden.map((bestand): Wachtend => {
      const keuring = keur(bestand, { alleenBeelden });
      let voorbeeld: string | undefined;
      if (!keuring.fout && keuring.soort?.beeld) {
        try {
          voorbeeld = URL.createObjectURL(bestand);
          voorbeelden.current.push(voorbeeld);
        } catch {
          // Geen voorbeeld te maken: dan staat er een icoon.
        }
      }
      volgnummer += 1;
      return { sleutel: `bestand-${volgnummer}`, bestand, keuring, naam: bestand.name, beschrijving: '', staat: 'wacht', voorbeeld };
    });
    setLijst((l) => [...l, ...nieuw]);
  };

  const klaarstaand = lijst.filter((w) => w.staat === 'wacht' && !w.keuring.fout);
  const afgerond = lijst.filter((w) => w.staat === 'gelukt');

  /* Eén voor één: over een trage verbinding is dat betrouwbaarder dan alles tegelijk, en bij elk
   * bestand staat dan wat ermee gebeurd is. */
  const upload = async () => {
    setBezig(true);
    let gelukt = 0;
    let mis = 0;
    for (const w of klaarstaand) {
      werkBij(w.sleutel, { staat: 'bezig', fout: undefined });
      try {
        const uitkomst = await uploaden.mutateAsync({ bestand: w.bestand, invoer: { naam: w.naam, beschrijving: w.beschrijving }, alleenBeelden });
        if (uitkomst.rij) {
          gelukt += 1;
          werkBij(w.sleutel, { staat: 'gelukt', pad: uitkomst.pad, inContent: padInContent(uitkomst.rij) });
        } else {
          mis += 1;
          werkBij(w.sleutel, { staat: 'zonder_rij', pad: uitkomst.pad, fout: uitkomst.zonderRij ?? 'onbekende fout' });
        }
      } catch (fout) {
        mis += 1;
        werkBij(w.sleutel, { staat: 'wacht', fout: redenVan(fout) });
      }
    }
    setBezig(false);
    if (mis === 0) toast({ title: gelukt === 1 ? '1 bestand geüpload' : `${gelukt} bestanden geüpload`, description: NIEUW_UITLEG });
    else toast({ title: 'Niet alles is gelukt', description: `${gelukt} geüpload, ${mis} niet. Bij elk bestand staat wat er mis ging.`, variant: 'destructive' });
  };

  /** Het bestand staat er al: alleen de rij in de bibliotheek nog. */
  const alsnog = async (w: Wachtend) => {
    setBezig(true);
    try {
      const rij = await rijMaken.mutateAsync({ pad: w.pad!, invoer: { naam: w.naam.trim() || w.bestand.name, beschrijving: w.beschrijving } });
      werkBij(w.sleutel, { staat: 'gelukt', inContent: padInContent(rij), fout: undefined });
      toast({ title: 'Staat nu in de bibliotheek', description: NIEUW_UITLEG });
    } catch (fout) {
      werkBij(w.sleutel, { fout: redenVan(fout) });
      toast({ title: 'Nog niet gelukt', description: redenVan(fout), variant: 'destructive' });
    } finally {
      setBezig(false);
    }
  };

  return (
    <div className="space-y-3" data-uploaden>
      <div
        className={`flex flex-wrap items-center gap-3 rounded-md border border-dashed p-3 ${boven ? 'border-primary bg-primary/5' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setBoven(true); }}
        onDragLeave={() => setBoven(false)}
        onDrop={(e) => { e.preventDefault(); setBoven(false); if (!bezig) voegToe(Array.from(e.dataTransfer.files)); }}
        data-sleepvak
      >
        <input ref={invoer} type="file" multiple accept={kiesbaar(alleenBeelden)} className="sr-only" tabIndex={-1} aria-label="Bestanden kiezen" disabled={bezig}
          onChange={(e) => { voegToe(Array.from(e.target.files ?? [])); e.target.value = ''; }} />
        <Button type="button" variant="outline" size="sm" disabled={bezig} onClick={() => invoer.current?.click()}>
          <Upload className="mr-1 h-4 w-4" /> Kies bestanden
        </Button>
        <p className="min-w-0 flex-1 basis-48 text-xs text-muted-foreground">
          Of sleep ze hierheen. {alleenBeelden ? 'Een beeld: png, jpg, webp, gif of avif' : 'Png, jpg, webp, gif, avif of pdf'}, hooguit 15 MB per bestand. Meer tegelijk kan.
        </p>
      </div>

      {lijst.length > 0 && (
        <>
          {klaarstaand.length > 0 && <p className="text-xs text-muted-foreground" data-uitleg="beschrijving">{BESCHRIJVING_BIJ_UPLOAD}</p>}
          <ul className="space-y-3">
            {lijst.map((w) => (
              // Op een telefoon staan de vakken onder het beeld, over de volle breedte; op een breder scherm ernaast.
              <li key={w.sleutel} className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-2 rounded-md border p-3 sm:grid-rows-[auto_1fr]" data-bestand={w.bestand.name} data-staat={w.keuring.fout ? 'geweigerd' : w.staat}>
                {w.voorbeeld
                  ? <img src={w.voorbeeld} alt="" className="h-12 w-16 self-start rounded border bg-muted object-contain sm:row-span-2 sm:h-16 sm:w-24" />
                  : <span className="flex h-12 w-16 items-center justify-center self-start rounded border bg-muted text-muted-foreground sm:row-span-2 sm:h-16 sm:w-24" aria-hidden><FileText className="h-6 w-6" /></span>}
                <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 self-center text-sm sm:self-start">
                  <span className="break-all font-medium">{w.bestand.name}</span>
                  <span className="text-xs text-muted-foreground">{grootte(w.bestand.size)}</span>
                </div>
                <div className="col-span-2 space-y-2 sm:col-span-1 sm:col-start-2">
                {w.keuring.fout && <p className="text-xs text-destructive" role="alert">{w.keuring.fout}</p>}

                {!w.keuring.fout && w.staat === 'wacht' && (
                  <>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1">
                        <Label htmlFor={`${w.sleutel}-naam`}>Naam</Label>
                        <Input id={`${w.sleutel}-naam`} value={w.naam} disabled={bezig} onChange={(e) => werkBij(w.sleutel, { naam: e.target.value })} />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor={`${w.sleutel}-beschrijving`}>Beschrijving</Label>
                        <Input id={`${w.sleutel}-beschrijving`} value={w.beschrijving} disabled={bezig} placeholder="Wat staat er op het beeld?" onChange={(e) => werkBij(w.sleutel, { beschrijving: e.target.value })} />
                      </div>
                    </div>
                    {w.keuring.letOp && (
                      <p className="flex items-start gap-1.5 text-xs text-amber-600" data-let-op="zwaar">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> <span>{w.keuring.letOp}</span>
                      </p>
                    )}
                    {w.fout && <p className="text-xs text-destructive" role="alert">{w.fout}</p>}
                    {/* Haalt de regel uit deze lijst: er is nog niets geüpload. */}
                    <Button type="button" variant="ghost" size="sm" className="-ml-3" disabled={bezig} onClick={() => setLijst((l) => l.filter((x) => x.sleutel !== w.sleutel))}>Niet uploaden</Button>
                  </>
                )}

                {w.staat === 'bezig' && <p className="text-xs text-muted-foreground">Bezig met uploaden…</p>}

                {w.staat === 'gelukt' && (
                  <div className="space-y-2">
                    <p className="flex items-start gap-1.5 text-xs text-emerald-700"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0" /> <span>Staat in de bibliotheek.</span></p>
                    <code className="block break-all text-xs" data-pad>{w.inContent}</code>
                    <div className="flex flex-wrap gap-2">
                      {kies && <Button type="button" size="sm" onClick={() => kies(w.inContent!)}>Gebruik dit beeld</Button>}
                      <KopieerPad pad={w.inContent!} />
                    </div>
                  </div>
                )}

                {w.staat === 'zonder_rij' && (
                  <div className="space-y-2">
                    <p className="flex items-start gap-1.5 text-xs text-destructive" role="alert">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> <span>{zonderRijZin(w.pad!, w.fout ?? 'onbekende fout')}</span>
                    </p>
                    <Button type="button" size="sm" disabled={bezig} onClick={() => alsnog(w)}>Zet alsnog in de bibliotheek</Button>
                  </div>
                )}
                </div>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {klaarstaand.length === 0 && !bezig && (afgerond.length > 0 || lijst.some((w) => w.keuring.fout)) && (
              // Als er niets meer te uploaden is. Haalt alleen de afgeronde en de geweigerde regels uit
              // deze lijst: wat is geüpload, blijft in de bibliotheek staan.
              <Button type="button" variant="ghost" size="sm" onClick={() => setLijst((l) => l.filter((w) => !w.keuring.fout && w.staat !== 'gelukt'))}>Klaar</Button>
            )}
            {klaarstaand.length > 0 && (
              <Button type="button" size="sm" disabled={bezig} onClick={upload}>
                <Upload className="mr-1 h-4 w-4" /> {bezig ? 'Uploaden…' : klaarstaand.length === 1 ? 'Upload 1 bestand' : `Upload ${klaarstaand.length} bestanden`}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ── De kiezer bij een afbeeldingsveld ─────────────────────────────────────── */

/** De knop "Kies uit media" met het venster erachter. `kies` krijgt het pad zoals het in de content hoort. */
export function MediaKiezer({ waarde, kies, uit }: { waarde: string; kies: (pad: string) => void; uit: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" disabled={uit} data-kies-uit-media><Images className="mr-1 h-4 w-4" /> Kies uit media</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-1.5rem)] max-w-4xl overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>Kies een beeld uit media</DialogTitle>
          <DialogDescription>
            Klik op een beeld om het in het veld te zetten. Je ziet hier alleen beelden: een pdf is geen afbeelding.
          </DialogDescription>
        </DialogHeader>
        {/* Pas lezen als het venster open is: een formulier heeft soms drie afbeeldingsvelden. */}
        {open && <Keuze waarde={waarde} kies={(pad) => { kies(pad); setOpen(false); }} />}
      </DialogContent>
    </Dialog>
  );
}

/** Een afbeeldingsveld: het pad, de knop "Kies uit media" en een klein voorbeeld. Kiezen vult het pad
 *  in; zelf typen blijft kunnen. Het voorbeeld komt rechtstreeks uit de opslag (data/media.ts). */
export function Beeldveld({ id, waarde, uit, zet }: { id: string; waarde: string; uit: boolean; zet: (pad: string) => void }) {
  return (
    <div className="flex items-start gap-3">
      <div className="min-w-0 flex-1 space-y-2">
        <Input id={id} value={waarde} disabled={uit} placeholder="/media/bestandsnaam.jpg" onChange={(e) => zet(e.target.value)} />
        <MediaKiezer waarde={waarde} uit={uit} kies={zet} />
      </div>
      {waarde.startsWith(VOORVOEGSEL) && (
        <img src={adresVanPad(waarde)} alt="" className="h-16 w-24 shrink-0 rounded border object-cover" />
      )}
    </div>
  );
}

function Keuze({ waarde, kies }: { waarde: string; kies: (pad: string) => void }) {
  const { magBewerken } = useRechten();
  const { data, error, isLoading } = useMedia();
  const [vraag, setVraag] = useState('');
  const [aantal, setAantal] = useState(STAP);
  const beelden = useMemo(() => (data ?? []).filter(isBeeld), [data]);
  const gevonden = useMemo(() => zoek(beelden, vraag), [beelden, vraag]);

  return (
    <div className="space-y-4" data-kiezer>
      {magBewerken && <Uploaden alleenBeelden kies={kies} />}
      <div className="space-y-1.5">
        <Label htmlFor="kiezer-zoek">Zoek in de media</Label>
        <Input id="kiezer-zoek" type="search" value={vraag} placeholder="Zoek op naam of beschrijving" onChange={(e) => setVraag(e.target.value)} />
      </div>
      {isLoading && <p className="text-sm text-muted-foreground">Laden…</p>}
      {error && <p className="text-sm text-destructive" role="alert">{(error as Error).message}</p>}
      {data && gevonden.length === 0 && <p className="text-sm text-muted-foreground">{beelden.length === 0 ? 'Er staan nog geen beelden in de media.' : 'Geen beeld gevonden met deze zoekwoorden.'}</p>}
      {gevonden.length > 0 && (
        <>
          <p className="text-xs text-muted-foreground" data-telling>{gevonden.length === beelden.length ? `${beelden.length} beelden, het nieuwste eerst` : `${gevonden.length} van ${beelden.length} beelden`}</p>
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {gevonden.slice(0, aantal).map((bestand) => {
              const gekozen = isGekozen(bestand, waarde);
              return (
                <li key={bestand.id}>
                  <button type="button" onClick={() => kies(padInContent(bestand))} aria-pressed={gekozen} data-kies={bestand.storage_path}
                    className={`block w-full rounded-md border p-1.5 text-left transition-colors hover:border-primary ${gekozen ? 'border-primary ring-2 ring-primary' : ''}`}>
                    <Miniatuur bestand={bestand} className="aspect-[4/3] w-full" />
                    <span className="mt-1 block truncate text-xs" title={naamVan(bestand)}>{naamVan(bestand)}</span>
                    {gekozen && <span className="block text-xs font-medium text-primary">Staat nu in het veld</span>}
                  </button>
                </li>
              );
            })}
          </ul>
          {gevonden.length > aantal && (
            <div className="flex justify-center">
              <Button type="button" variant="outline" size="sm" onClick={() => setAantal((a) => a + STAP)}>Toon er nog {Math.min(STAP, gevonden.length - aantal)}</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
